import { useCompoundItem } from '@mui/base/useCompound';
import { AccordionItem } from '@radix-ui/react-accordion';
import cn from 'classnames';
import { forwardRef, useMemo, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { useAccordionGroupContext } from '../accordion-group/accordion-group-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { AccordionProps } from './accordion.types';

import './accordion.scss';

const accordionValueGenerator = (otherAccordionValues: Set<number | string>): number => {
  return otherAccordionValues.size;
};

/**
 * The `Accordion` component represents an individual accordion item.
 * It contains `AccordionHeader` and `AccordionContent` components to define the
 * header and the content of the accordion.
 *
 * ### Usage
 * Use the `Accordion` component to create a collapsible section with a header and content.
 *
 * ```tsx
 * import { AccordionGroup, Accordion, AccordionHeader, AccordionContent } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <AccordionGroup>
 *       <Accordion value="banana-facts">
 *         <AccordionHeader>Banana facts</AccordionHeader>
 *         <AccordionContent>
 *           Bananas are a great source of potassium and can help replenish electrolytes after exercise.
 *         </AccordionContent>
 *       </Accordion>
 *       <Accordion>...</Accordion>
 *       <Accordion>...</Accordion>
 *     </AccordionGroup>
 *   );
 * }
 * ```
 */
const Accordion = forwardRef(
  (props: AccordionProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      value: valueParam,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const accordionGroupContextValue = useAccordionGroupContext();
    if (accordionGroupContextValue === null) {
      throw new Error('No AccordionContext provided');
    }

    const ref = useRef<HTMLElement>(null);
    const handleRef = mergeRefs([ref, forwardedRef]);

    const metadata = useMemo(() => ({ ref }), []);

    const { id: value } = useCompoundItem(valueParam ?? accordionValueGenerator, metadata);
    const accordionValue = valueParam ?? `accordion-item-${value}`;

    const { disableDivider, styling } = accordionGroupContextValue;

    const classes = cn(
      'rbui-accordion',
      {
        'rbui-accordion--disable-divider': disableDivider,
        'rbui-accordion--styled': styling === 'styled',
      },
      className,
    );

    return (
      <AccordionItem className={classes} ref={handleRef} value={accordionValue} {...rest}>
        {children}
      </AccordionItem>
    );
  },
);

Accordion.displayName = 'Accordion';

export default Accordion;
